const rootUrl = import.meta.env.VITE_BACKEND_API_ROOT_URL;

const PUSH_SERVICE_WORKER_URL = "/scripts/web-push/push-serviceWorker.js";
const PUSH_SERVICE_WORKER_SCOPE = "/scripts/web-push/";

let applicationServerPublicKey;

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray;
}

async function retrievePublicKey(accessToken) {
  const response = await fetch(`${rootUrl}api/notifications/public-key`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }

  const applicationServerPublicKeyBase64 = await response.text();
  return urlB64ToUint8Array(applicationServerPublicKeyBase64);
}

async function storePushSubscription(accessToken, pushSubscription) {
  return fetch(`${rootUrl}api/notifications/subscriptions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(pushSubscription),
  });
}

async function discardPushSubscription(accessToken, pushSubscription) {
  return fetch(
    `${rootUrl}api/notifications/subscriptions?endpoint=${encodeURIComponent(
      pushSubscription.endpoint
    )}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );
}

export function isPushNotificationSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function registerPushServiceWorker() {
  if (!isPushNotificationSupported()) {
    throw new Error("Push notifications are not supported");
  }

  return navigator.serviceWorker.register(PUSH_SERVICE_WORKER_URL, {
    scope: PUSH_SERVICE_WORKER_SCOPE,
  });
}

export async function getPushSubscriptionStatus() {
  const registration = await registerPushServiceWorker();
  const subscription = await registration.pushManager.getSubscription();

  return {
    registration,
    subscription,
    isSubscribed: subscription !== null,
    notificationsBlocked: Notification.permission === "denied",
  };
}

export async function subscribeToPushNotifications({
  accessToken,
  registration,
}) {
  const activeRegistration =
    registration ?? (await registerPushServiceWorker());

  if (!applicationServerPublicKey) {
    applicationServerPublicKey = await retrievePublicKey(accessToken);
  }

  const pushSubscription = await activeRegistration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerPublicKey,
  });

  const response = await storePushSubscription(accessToken, pushSubscription);
  if (!response.ok) {
    throw new Error(
      "Failed to store the Push Notifications subscription on server"
    );
  }

  return pushSubscription;
}

export async function unsubscribeFromPushNotifications({
  accessToken,
  registration,
}) {
  const activeRegistration =
    registration ?? (await registerPushServiceWorker());
  const pushSubscription =
    await activeRegistration.pushManager.getSubscription();

  if (!pushSubscription) {
    return false;
  }

  await pushSubscription.unsubscribe();

  const response = await discardPushSubscription(accessToken, pushSubscription);
  if (!response.ok) {
    throw new Error(
      "Failed to discard the Push Notifications subscription from server"
    );
  }

  return true;
}
